import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import EnrollmentModal from './EnrollmentModal';
import LoginModal from './LoginModal';

interface Course {
  id: string;
  title: string;
  category: string;
  duration: string;
  modules: number;
  image: string;
  description?: string;
  type: string;
  brand?: string;
}

interface PrototypeCourseDetailProps {
  course: Course;
}

const moduleTopics: Record<string, string[]> = {
  pb1: [
    'Quem são os povos indígenas do Brasil',
    'Territorialidade e cosmovisão indígena',
    'Marcos legais: da Constituição à FUNAI',
    'Diversidade linguística e cultural',
    'Desafios contemporâneos',
    'Povos indígenas e educação escolar',
  ],
  pb2: [
    'O que são quilombos: origem histórica',
    'Resistência negra no período colonial',
    'Comunidades quilombolas hoje',
    'Territórios quilombolas e a luta pela terra',
    'Cultura, arte e identidade quilombola',
  ],
  pb3: [
    'África: berço da humanidade e da cultura brasileira',
    'Religiões de matriz africana no Brasil',
    'Língua, culinária e estética afro-brasileira',
    'Música e dança: do batuque ao samba',
    'Capoeira como patrimônio cultural',
    'Contribuições africanas na ciência e tecnologia',
    'Representatividade e visibilidade negra',
  ],
  pb4: [
    'Os povos da Amazônia e do Cerrado',
    'Saberes sobre plantas medicinais e alimentação',
    'Manejo sustentável da floresta',
    'Rios como estradas e fonte de vida',
    'Ameaças e resistência dos povos da floresta',
    'Diálogos entre saber tradicional e ciência',
  ],
  pb5: [
    'Festas juninas e as tradições nordestinas',
    'Bumba meu boi e Maracatu',
    'Carnaval: origem e diversidade regional',
    'Folguedos e brincadeiras populares',
  ],
  pb6: [
    'O que é racismo e como ele opera na escola',
    'Lei 10.639/03: obrigatoriedade e significado',
    'Currículos antirracistas na prática',
    'Representatividade nos materiais didáticos',
    'Relações étnico-raciais na educação infantil',
    'Como lidar com situações de racismo na escola',
    'Projetos pedagógicos para a diversidade',
    'Avaliação e monitoramento das ações',
  ],
};

// Boneco (character) images per course
const courseCharacters: Record<string, { image: string; name: string; description: string }> = {
  pb1: {
    image: 'https://public.readdy.ai/ai/img_res/3b313d2e9e85f3f8a36f92b7507a8e84.jpg',
    name: 'Iru',
    description: 'Representa os mais de 300 povos indígenas do Brasil',
  },
  pb2: {
    image: 'https://readdy.ai/api/search-image?query=Single%20folk%20art%20illustrated%20character%20doll%20Afro-Brazilian%20quilombola%20woman%20standing%20full%20body%20pose%2C%20wearing%20vibrant%20colorful%20patchwork%20dress%20with%20yellow%20orange%20and%20green%2C%20head%20wrap%20turban%2C%20beaded%20jewelry%2C%20flat%20illustration%20boneco%20paper%20doll%20style%2C%20isolated%20on%20warm%20ochre%20plain%20background%2C%20hand-drawn%20naive%20folk%20art%20quality%2C%20warm%20earthy%20tones%20vibrant%20accents%2C%20centered%20figure&width=420&height=520&seq=boneco-pb2&orientation=portrait',
    name: 'Dandara',
    description: 'Homenagem à resistência e à memória quilombola',
  },
  pb3: {
    image: 'https://readdy.ai/api/search-image?query=Single%20folk%20art%20illustrated%20character%20doll%20Afro-Brazilian%20woman%20in%20ceremonial%20attire%20standing%20full%20body%20pose%2C%20white%20and%20gold%20layered%20skirt%20dress%2C%20colorful%20bead%20necklaces%20ekeke%2C%20hair%20wrapped%2C%20flat%20illustration%20boneco%20paper%20doll%20style%2C%20isolated%20on%20deep%20terracotta%20warm%20background%2C%20hand-drawn%20naive%20folk%20art%20quality%2C%20cultural%20religious%20African%20Brazilian%20elements%2C%20centered%20figure&width=420&height=520&seq=boneco-pb3&orientation=portrait',
    name: 'Oxum',
    description: 'Símbolo das matrizes culturais africanas no Brasil',
  },
  pb4: {
    image: 'https://readdy.ai/api/search-image?query=Single%20folk%20art%20illustrated%20character%20doll%20Amazon%20forest%20river%20community%20person%20standing%20full%20body%20pose%2C%20wearing%20natural%20fiber%20woven%20clothing%2C%20holding%20medicinal%20plants%20and%20wooden%20paddle%2C%20simple%20earthy%20tones%20clothing%2C%20flat%20illustration%20boneco%20paper%20doll%20style%2C%20isolated%20on%20deep%20forest%20green%20plain%20background%2C%20hand-drawn%20naive%20folk%20art%20quality%2C%20nature%20botanical%20elements%2C%20centered%20figure&width=420&height=520&seq=boneco-pb4&orientation=portrait',
    name: 'Cauã',
    description: 'Guardião dos saberes tradicionais da floresta',
  },
  pb5: {
    image: 'https://readdy.ai/api/search-image?query=Single%20folk%20art%20illustrated%20character%20doll%20Brazilian%20festive%20celebration%20person%20standing%20full%20body%20pose%2C%20wearing%20colorful%20bumba%20meu%20boi%20festive%20costume%20with%20orange%20yellow%20ribbons%20confetti%20hat%2C%20big%20smile%2C%20flat%20illustration%20boneco%20paper%20doll%20style%2C%20isolated%20on%20bright%20golden%20ochre%20plain%20background%2C%20hand-drawn%20naive%20folk%20art%20quality%2C%20festive%20joyful%20carnival%20elements%2C%20centered%20figure&width=420&height=520&seq=boneco-pb5&orientation=portrait',
    name: 'Boi',
    description: 'Alegria das festas e manifestações populares do Brasil',
  },
  pb6: {
    image: 'https://readdy.ai/api/search-image?query=Single%20folk%20art%20illustrated%20character%20doll%20Brazilian%20teacher%20educator%20standing%20full%20body%20pose%2C%20wearing%20colorful%20casual%20clothes%20holding%20open%20book%20with%20cultural%20symbols%2C%20warm%20friendly%20expression%2C%20diverse%20representation%2C%20flat%20illustration%20boneco%20paper%20doll%20style%2C%20isolated%20on%20warm%20terracotta%20plain%20background%2C%20hand-drawn%20naive%20folk%20art%20quality%2C%20educational%20cultural%20elements%2C%20centered%20figure&width=420&height=520&seq=boneco-pb6&orientation=portrait',
    name: 'Mara',
    description: 'Educadora comprometida com a diversidade na escola',
  },
};

// All series characters for the gallery strip
const allSeriesCharacters = [
  { id: 'pb1', label: 'Indígenas', color: '#C85C2A' },
  { id: 'pb2', label: 'Quilombolas', color: '#2D6A4F' },
  { id: 'pb3', label: 'Afro-Brasileira', color: '#C85C2A' },
  { id: 'pb4', label: 'Floresta', color: '#2D6A4F' },
  { id: 'pb5', label: 'Festas', color: '#E8A42A' },
  { id: 'pb6', label: 'Educação', color: '#C85C2A' },
];

export default function PrototypeCourseDetail({ course }: PrototypeCourseDetailProps) {
  const navigate = useNavigate();
  const [expandedModule, setExpandedModule] = useState<number | null>(null);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [userData, setUserData] = useState<{ name: string; email: string } | null>(null);

  const isPovos = course.brand === 'povos-do-brasil';
  const primary = isPovos ? '#C85C2A' : '#CC1B1B';
  const accent = isPovos ? '#E8A42A' : '#FFCC00';

  const topics = moduleTopics[course.id] ?? Array.from({ length: course.modules }, (_, i) => `Módulo ${i + 1} — Em desenvolvimento`);
  const character = courseCharacters[course.id];

  useEffect(() => {
    const storedUser = localStorage.getItem('courseUser');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      setUserData(user);
      const enrollmentKey = `enrollment_proto_${course.id}`;
      const enrolled = localStorage.getItem(enrollmentKey) === 'true';
      setIsRegistered(enrolled);
    }
  }, [course.id]);

  const handleEnroll = (newUserData: { name: string; email: string; phone: string; institution: string; password: string }) => {
    setUserData(newUserData);
    setIsRegistered(true);
    localStorage.setItem(`enrollment_proto_${course.id}`, 'true');
  };

  const handleLogin = (loggedUser: { name: string; email: string; phone: string; password: string }) => {
    setUserData(loggedUser);
    setIsRegistered(true);
    localStorage.setItem(`enrollment_proto_${course.id}`, 'true');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Prototype Banner */}
      <div className="bg-[#E8A42A]/15 border-b border-[#E8A42A]/40 pt-20">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-3">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-draft-line text-[#C85C2A] text-lg"></i>
          </div>
          <p className="text-sm text-[#7A3010] font-medium">
            <strong>Versão protótipo</strong> — Este curso está em desenvolvimento. O conteúdo final poderá ser alterado.
          </p>
        </div>
      </div>

      {/* Hero */}
      <div className="pb-0 bg-gradient-to-br from-[#FBF5ED] to-white">
        <div className="max-w-7xl mx-auto px-6 pt-8 pb-12">
          {/* Brand identity bar */}
          <div className="flex items-center gap-2 mb-6">
            <div className="w-3 h-3 rounded-full bg-[#C85C2A]"></div>
            <div className="w-3 h-3 rounded-full bg-[#E8A42A]"></div>
            <div className="w-3 h-3 rounded-full bg-[#2D6A4F]"></div>
            <span className="text-xs font-bold text-[#C85C2A] tracking-widest uppercase ml-1">Série Povos do Brasil</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left */}
            <div>
              <div className="inline-block bg-[#C85C2A] text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                {course.category}
              </div>

              <h1 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">
                {course.title}
              </h1>

              {course.description && (
                <p className="text-gray-600 text-base mb-8 leading-relaxed">{course.description}</p>
              )}

              {/* Registration CTA */}
              <div className="flex flex-wrap gap-3 mb-8">
                {isRegistered ? (
                  <div className="flex items-center gap-3 bg-[#2D6A4F]/10 border border-[#2D6A4F]/30 text-[#2D6A4F] px-6 py-3 rounded-xl font-semibold text-sm">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#2D6A4F] rounded-full flex-shrink-0">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <p className="font-bold text-[#1A4A30]">Inscrição confirmada!</p>
                      <p className="text-xs text-[#2D6A4F]/80 font-normal">Olá, {userData?.name?.split(' ')[0]}! Você será avisado no lançamento.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => setShowEnrollModal(true)}
                      className="flex items-center gap-2 text-white font-bold px-8 py-3 rounded-xl transition-all hover:opacity-90 whitespace-nowrap cursor-pointer"
                      style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }}
                    >
                      <i className="ri-user-add-line"></i>
                      Quero ser avisado
                    </button>
                    <button
                      onClick={() => setShowLoginModal(true)}
                      className="border-2 text-sm font-semibold px-6 py-3 rounded-xl transition-all whitespace-nowrap cursor-pointer hover:bg-[#C85C2A]/5"
                      style={{ borderColor: primary, color: primary }}
                    >
                      <i className="ri-login-circle-line mr-2"></i>
                      Já tenho cadastro
                    </button>
                  </>
                )}
                <button
                  onClick={() => navigate(-1)}
                  className="border-2 border-gray-200 text-gray-500 text-sm font-semibold px-6 py-3 rounded-xl transition-all whitespace-nowrap cursor-pointer hover:border-gray-300"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Voltar aos cursos
                </button>
              </div>

              {/* Course info */}
              <div className="grid grid-cols-2 gap-4 bg-white p-5 rounded-2xl border border-[#C85C2A]/15">
                {[
                  { icon: 'ri-computer-line', label: 'Autoformativo' },
                  { icon: 'ri-medal-line', label: 'Com certificado' },
                  { icon: 'ri-global-line', label: 'Português do Brasil' },
                  { icon: 'ri-time-line', label: course.duration },
                  { icon: 'ri-book-open-line', label: `${course.modules} módulos` },
                  { icon: 'ri-draft-line', label: 'Em desenvolvimento' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#C85C2A]/10 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-xl`} style={{ color: primary }}></i>
                    </div>
                    <span className="text-sm text-gray-600">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Character boneco */}
            {character ? (
              <div className="flex flex-col items-center">
                {/* Character card */}
                <div className="relative w-full max-w-sm mx-auto">
                  {/* Decorative background shape */}
                  <div
                    className="absolute inset-0 rounded-3xl"
                    style={{ background: 'linear-gradient(145deg, #FBF5ED 0%, #F5E6D0 100%)' }}
                  ></div>
                  {/* Dot pattern decoration */}
                  <div className="absolute top-4 right-4 grid grid-cols-4 gap-1.5 opacity-30">
                    {Array.from({ length: 16 }).map((_, i) => (
                      <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#C85C2A]"></div>
                    ))}
                  </div>
                  <div className="absolute bottom-4 left-4 grid grid-cols-4 gap-1.5 opacity-30">
                    {Array.from({ length: 16 }).map((_, i) => (
                      <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#2D6A4F]"></div>
                    ))}
                  </div>

                  {/* Character image */}
                  <div className="relative z-10 flex flex-col items-center pt-8 pb-6 px-6">
                    <div className="w-56 h-72 rounded-2xl overflow-hidden mb-5 border-4 border-white" style={{ boxShadow: '0 8px 32px rgba(200,92,42,0.18)' }}>
                      <img
                        src={character.image}
                        alt={`Personagem ${character.name}`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>

                    {/* Character name badge */}
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-[#C85C2A]"></div>
                      <span className="text-lg font-bold text-gray-900">{character.name}</span>
                      <div className="w-2 h-2 rounded-full bg-[#E8A42A]"></div>
                    </div>
                    <p className="text-xs text-gray-500 text-center leading-relaxed max-w-xs">
                      {character.description}
                    </p>

                    {/* Video in production badge */}
                    <div className="mt-4 inline-flex items-center gap-2 bg-white border border-[#E8A42A]/60 text-[#7A3010] px-4 py-2 rounded-full text-xs font-semibold">
                      <i className="ri-vidicon-line text-[#E8A42A] text-sm"></i>
                      Vídeo em produção
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Fallback original */
              <div className="relative rounded-3xl overflow-hidden" style={{ minHeight: '340px' }}>
                <img
                  src={course.image}
                  alt={course.title}
                  className="w-full h-full object-cover object-top"
                  style={{ minHeight: '340px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A0A00]/80 via-[#1A0A00]/30 to-transparent flex flex-col items-center justify-end p-8">
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/40 text-white px-5 py-2.5 rounded-full text-sm font-bold">
                      <i className="ri-vidicon-line text-[#E8A42A]"></i>
                      Vídeo em produção
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Color strip */}
      <div className="h-1.5 flex">
        <div className="flex-1 bg-[#C85C2A]"></div>
        <div className="flex-1 bg-[#E8A42A]"></div>
        <div className="flex-1 bg-[#2D6A4F]"></div>
        <div className="flex-1 bg-[#E8A42A]"></div>
        <div className="flex-1 bg-[#C85C2A]"></div>
      </div>

      {/* Characters gallery strip */}
      <div className="bg-[#2D1A0A] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-[#E8A42A] text-xs font-bold tracking-widest uppercase text-center mb-6">
            Conheça todos os personagens da série
          </p>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {allSeriesCharacters.map((char) => {
              const charData = courseCharacters[char.id];
              const isActive = char.id === course.id;
              return (
                <div
                  key={char.id}
                  className={`flex flex-col items-center gap-2 transition-all ${isActive ? 'scale-105' : 'opacity-60 hover:opacity-90'}`}
                >
                  <div
                    className={`w-16 h-20 rounded-xl overflow-hidden border-2 transition-all ${isActive ? 'border-[#E8A42A]' : 'border-white/20'}`}
                  >
                    {charData && (
                      <img
                        src={charData.image}
                        alt={charData.name}
                        className="w-full h-full object-cover object-top"
                      />
                    )}
                  </div>
                  <div className="text-center">
                    {charData && (
                      <p className="text-white text-xs font-bold">{charData.name}</p>
                    )}
                    <p className="text-white/50 text-xs">{char.label}</p>
                  </div>
                  {isActive && (
                    <div className="w-4 h-0.5 rounded-full" style={{ backgroundColor: '#E8A42A' }}></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Modules section */}
      <div className="py-16 bg-[#FBF5ED]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-8 h-8 flex items-center justify-center bg-[#C85C2A] rounded-lg">
              <i className="ri-book-open-line text-white text-lg"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Conteúdo programático</h2>
            <div className="ml-auto bg-[#E8A42A]/20 text-[#7A3010] px-3 py-1 rounded-full text-xs font-bold">
              Rascunho
            </div>
          </div>

          {/* Registration gate for modules */}
          {!isRegistered ? (
            <div className="relative">
              {/* Preview — first 2 modules visible but faded */}
              <div className="space-y-3 opacity-40 pointer-events-none select-none">
                {topics.slice(0, 2).map((topic, index) => (
                  <div key={index} className="bg-white border-2 border-[#C85C2A]/10 rounded-xl p-5 flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#C85C2A]/10 text-[#C85C2A] font-bold text-sm flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-bold text-gray-900">{topic}</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Módulo {index + 1} • Em desenvolvimento</p>
                    </div>
                    <span className="bg-[#E8A42A]/20 text-[#7A3010] px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap">Em breve</span>
                  </div>
                ))}
                {topics.slice(2, 4).map((topic, index) => (
                  <div key={index + 2} className="bg-white border-2 border-[#C85C2A]/10 rounded-xl p-5 flex items-center gap-4 blur-sm">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#C85C2A]/10 text-[#C85C2A] font-bold text-sm flex-shrink-0">
                      {index + 3}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-bold text-gray-900">{topic}</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Módulo {index + 3} • Em desenvolvimento</p>
                    </div>
                    <span className="bg-[#E8A42A]/20 text-[#7A3010] px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap">Em breve</span>
                  </div>
                ))}
              </div>

              {/* Lock overlay */}
              <div className="mt-6 rounded-2xl overflow-hidden border-2 border-[#C85C2A]/20">
                <div className="bg-gradient-to-br from-[#FBF5ED] to-[#F5E6D0] p-8 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-white rounded-full mx-auto mb-4 border-2 border-[#C85C2A]/20">
                    <i className="ri-lock-line text-3xl text-[#C85C2A]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Cadastre-se para acompanhar o curso
                  </h3>
                  <p className="text-gray-600 text-sm mb-6 max-w-sm mx-auto leading-relaxed">
                    Faça seu cadastro gratuito e seja notificado assim que este curso estiver disponível. Você também terá acesso antecipado ao conteúdo.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <button
                      onClick={() => setShowEnrollModal(true)}
                      className="flex items-center gap-2 text-white font-bold px-8 py-3 rounded-xl transition-all hover:opacity-90 whitespace-nowrap cursor-pointer w-full sm:w-auto justify-center"
                      style={{ background: `linear-gradient(135deg, #C85C2A, #E8A42A)` }}
                    >
                      <i className="ri-user-add-line"></i>
                      Fazer cadastro gratuito
                    </button>
                    <button
                      onClick={() => setShowLoginModal(true)}
                      className="border-2 border-[#C85C2A] text-[#C85C2A] font-bold px-8 py-3 rounded-xl hover:bg-[#C85C2A]/5 transition-all whitespace-nowrap cursor-pointer w-full sm:w-auto"
                    >
                      <i className="ri-login-circle-line mr-2"></i>
                      Já tenho cadastro
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-4">
                    <i className="ri-shield-check-line mr-1"></i>
                    Gratuito • Sem spam • Cancele quando quiser
                  </p>
                </div>
              </div>

              {/* Remaining count hint */}
              {topics.length > 4 && (
                <p className="text-center text-xs text-gray-400 mt-4">
                  + {topics.length - 4} outros módulos disponíveis após o cadastro
                </p>
              )}
            </div>
          ) : (
            /* Full modules list for registered users */
            <div className="space-y-3">
              {/* Welcome back banner */}
              <div className="bg-[#2D6A4F]/10 border border-[#2D6A4F]/25 rounded-xl p-4 flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#2D6A4F] rounded-full flex-shrink-0">
                  <i className="ri-check-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-[#1A4A30]">Você está inscrito!</p>
                  <p className="text-xs text-[#2D6A4F]/80">Quando este curso lançar, você será o primeiro a saber, {userData?.name?.split(' ')[0]}.</p>
                </div>
              </div>

              {topics.map((topic, index) => (
                <div
                  key={index}
                  className="bg-white border-2 border-[#C85C2A]/10 rounded-xl overflow-hidden transition-all"
                >
                  <button
                    onClick={() => setExpandedModule(expandedModule === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-[#FBF5ED] transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 bg-[#C85C2A]/10 text-[#C85C2A] font-bold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-gray-900">{topic}</h3>
                        <p className="text-xs text-gray-400 mt-0.5">Módulo {index + 1} • Em desenvolvimento</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                      <span className="bg-[#E8A42A]/20 text-[#7A3010] px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                        Em breve
                      </span>
                      <i className={`text-gray-400 text-lg ${expandedModule === index ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                    </div>
                  </button>

                  {expandedModule === index && (
                    <div className="px-5 pb-5 border-t border-[#C85C2A]/10">
                      <div className="mt-4 flex items-start gap-3 bg-[#FBF5ED] rounded-xl p-4">
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                          <i className="ri-information-line text-[#C85C2A] text-xl"></i>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#7A3010] mb-1">Conteúdo em produção</p>
                          <p className="text-xs text-gray-500 leading-relaxed">
                            Este módulo está sendo desenvolvido pela equipe da Editora LT.
                            O material incluirá vídeos, leituras complementares e atividades práticas.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* CTA at bottom */}
          <div className="mt-10 rounded-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#C85C2A] to-[#2D6A4F] p-8 text-center">
              <div className="flex justify-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-white/60"></div>
                <div className="w-3 h-3 rounded-full bg-[#E8A42A]"></div>
                <div className="w-3 h-3 rounded-full bg-white/60"></div>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Quer acompanhar o lançamento?</h3>
              <p className="text-white/80 text-sm mb-5">
                Seja o primeiro a saber quando este curso estiver disponível.
              </p>
              {isRegistered ? (
                <div className="inline-flex items-center gap-2 bg-white/20 text-white px-6 py-3 rounded-xl font-semibold text-sm">
                  <i className="ri-check-circle-fill text-[#E8A42A] text-lg"></i>
                  Você será notificado no lançamento!
                </div>
              ) : (
                <button
                  onClick={() => setShowEnrollModal(true)}
                  className="bg-white text-[#C85C2A] font-bold px-8 py-3 rounded-xl hover:bg-[#FBF5ED] transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-user-add-line mr-2"></i>
                  Fazer cadastro gratuito
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <EnrollmentModal
        isOpen={showEnrollModal}
        onClose={() => setShowEnrollModal(false)}
        onEnroll={handleEnroll}
        courseName={course.title}
      />

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={handleLogin}
        onSwitchToEnroll={() => { setShowLoginModal(false); setShowEnrollModal(true); }}
        courseId={course.id}
      />
    </div>
  );
}
